import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Trophy, Star, Crown, Gamepad2, BookOpen } from "lucide-react";
import { LEVELS } from "@/data/levels";
import ShareCard from "@/components/site/ShareCard";
import YourBest from "@/components/site/YourBest";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      day: "numeric",
      month: "short",
    });
  } catch {
    return "";
  }
}

function Medal({ rank }) {
  const styles = [
    "bg-sun text-tiger-deep",
    "bg-tiger-cream text-tiger-deep",
    "bg-tiger-orange text-white",
  ];
  const cls = styles[rank] || "bg-white text-tiger-deep";
  return (
    <span
      className={`grid h-10 w-10 flex-shrink-0 place-items-center rounded-full border-[3px] border-tiger-deep font-heading text-lg font-bold shadow-sticker ${cls}`}
    >
      {rank < 3 ? ["🥇", "🥈", "🥉"][rank] : rank + 1}
    </span>
  );
}

function StarRow({ count }) {
  return (
    <span className="inline-flex items-center gap-0.5">
      {[0, 1, 2].map((i) => (
        <Star
          key={i}
          className={`h-4 w-4 ${
            i < count ? "fill-sun text-sun" : "text-tiger-deep/20"
          }`}
        />
      ))}
    </span>
  );
}

export default function Leaderboard() {
  const [quiz, setQuiz] = useState([]);
  const [game, setGame] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [q, g] = await Promise.all([
          axios.get(`${API}/quiz/leaderboard?limit=10`),
          axios.get(`${API}/game/leaderboard?limit=10`),
        ]);
        if (!mounted) return;
        setQuiz(q.data || []);
        setGame(g.data || []);
      } catch (e) {
        console.error("Leaderboard fetch failed", e);
        if (mounted) setError("Could not load the leaderboard right now.");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <div className="min-h-screen paper" data-testid="leaderboard-page">
      <header className="sticky top-0 z-30 border-b-[3px] border-tiger-deep bg-tiger-sand/95 backdrop-blur-md">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6">
          <Link
            to="/"
            className="inline-flex items-center gap-2 rounded-full border-[3px] border-tiger-deep bg-white px-4 py-2 font-heading text-base font-bold text-tiger-deep shadow-sticker hover:-translate-y-0.5 hover:shadow-stickerLg transition-transform"
            data-testid="back-to-home"
          >
            <ArrowLeft className="h-4 w-4" /> Back
          </Link>
          <p className="hidden sm:flex items-center gap-2 font-heading text-xl font-bold text-tiger-deep">
            <Crown className="h-5 w-5 text-tiger-orange" /> Tiger Hero
            Leaderboard
          </p>
          <Link
            to="/game"
            className="inline-flex items-center gap-2 rounded-full border-[3px] border-tiger-deep bg-tiger-orange px-4 py-2 font-heading text-sm font-bold text-white shadow-sticker"
            data-testid="leaderboard-play-game"
          >
            <Gamepad2 className="h-4 w-4" /> Play
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-10 sm:px-6 sm:py-14">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 rounded-full border-[3px] border-tiger-deep bg-white px-4 py-1.5 font-body text-sm font-bold text-tiger-deep shadow-sticker">
            <Trophy className="h-4 w-4 text-tiger-orange" /> Top Tiger Heroes
          </div>
          <h1 className="mt-4 font-heading text-4xl font-bold text-tiger-deep sm:text-5xl">
            Hall of Heroes 🏆
          </h1>
          <p className="mx-auto mt-3 max-w-xl font-body text-base text-tiger-deep sm:text-lg">
            See who knows the most about tigers and who survived the most
            adventures!
          </p>
        </div>

        {error && (
          <div
            className="mx-auto mt-8 max-w-md rounded-2xl border-[3px] border-tiger-deep bg-red-100 p-4 text-center font-body text-base font-bold text-tiger-deep shadow-sticker"
            data-testid="leaderboard-error"
          >
            {error}
          </div>
        )}

        <div className="mt-10">
          <YourBest
            name={
              typeof window !== "undefined"
                ? localStorage.getItem("tiger_player_name") || ""
                : ""
            }
          />
        </div>

        <div className="mt-10 grid gap-8 lg:grid-cols-2">
          {/* Quiz leaderboard */}
          <Board
            testid="quiz-board"
            icon={<BookOpen className="h-6 w-6" />}
            color="bg-jungle-leaf"
            title="Tiger Quiz Top 10"
            subtitle="5-question Fun Facts Quiz"
            empty="No scores yet — take the quiz on the home page!"
            loading={loading}
            rows={quiz}
            renderRow={(row, i) => (
              <Row
                key={row.id}
                rank={i}
                name={row.name}
                primary={
                  <span
                    className="rounded-full border-[3px] border-tiger-deep bg-sun px-3 py-0.5 font-heading text-sm font-bold text-tiger-deep"
                  >
                    {row.score}/{row.total}
                  </span>
                }
                meta={formatDate(row.timestamp)}
              />
            )}
            footerLink={{ to: "/#fun-facts", label: "Take the quiz →" }}
          />

          {/* Game leaderboard */}
          <Board
            testid="game-board"
            icon={<Gamepad2 className="h-6 w-6" />}
            color="bg-tiger-orange"
            title="Tiger Game Top 10"
            subtitle="Highest score across 10 levels"
            empty="No scores yet — be the first to play!"
            loading={loading}
            rows={game}
            renderRow={(row, i) => {
              const level = LEVELS[row.level_index];
              return (
                <Row
                  key={row.id}
                  rank={i}
                  name={row.name}
                  primary={
                    <span className="rounded-full border-[3px] border-tiger-deep bg-jungle-leaf px-3 py-0.5 font-heading text-sm font-bold text-white">
                      ⭐ {row.score}
                    </span>
                  }
                  meta={
                    <span className="flex items-center gap-2 text-xs">
                      <span>
                        {level?.emoji ?? ""} {row.level_name}
                      </span>
                      <StarRow count={row.stars} />
                    </span>
                  }
                />
              );
            }}
            footerLink={{ to: "/game", label: "Play the game →" }}
          />
        </div>

        <div className="mt-10">
          <ShareCard
            title="Challenge a friend! 🐯"
            subtitle="Send Tigersforkids to a buddy and see who tops the leaderboard."
            playerName={
              typeof window !== "undefined"
                ? localStorage.getItem("tiger_player_name") || ""
                : ""
            }
          />
        </div>
      </main>
    </div>
  );
}

function Board({
  testid,
  icon,
  color,
  title,
  subtitle,
  rows,
  loading,
  empty,
  renderRow,
  footerLink,
}) {
  return (
    <section
      className="rounded-3xl border-[4px] border-tiger-deep bg-white p-5 shadow-stickerLg sm:p-7"
      data-testid={testid}
    >
      <div className="flex items-center gap-3">
        <span
          className={`grid h-12 w-12 place-items-center rounded-2xl border-[3px] border-tiger-deep ${color} text-white shadow-sticker`}
        >
          {icon}
        </span>
        <div>
          <h2 className="font-heading text-2xl font-bold text-tiger-deep">
            {title}
          </h2>
          <p className="font-body text-sm text-tiger-deep/70">{subtitle}</p>
        </div>
      </div>

      <div className="mt-5 space-y-2">
        {loading && (
          <div
            className="rounded-2xl border-[3px] border-dashed border-tiger-deep/40 bg-tiger-cream/50 px-4 py-6 text-center font-body text-sm font-bold text-tiger-deep/60"
            data-testid={`${testid}-loading`}
          >
            Loading scores…
          </div>
        )}
        {!loading && rows.length === 0 && (
          <div
            className="rounded-2xl border-[3px] border-dashed border-tiger-deep/40 bg-tiger-cream/50 px-4 py-6 text-center font-body text-sm font-bold text-tiger-deep/70"
            data-testid={`${testid}-empty`}
          >
            {empty}
          </div>
        )}
        {!loading && rows.map(renderRow)}
      </div>

      <div className="mt-5 text-center">
        <Link
          to={footerLink.to}
          className="inline-block rounded-full border-[3px] border-tiger-deep bg-tiger-cream px-5 py-2 font-heading text-sm font-bold text-tiger-deep shadow-sticker hover:-translate-y-0.5 hover:shadow-stickerLg transition-transform"
        >
          {footerLink.label}
        </Link>
      </div>
    </section>
  );
}

function Row({ rank, name, primary, meta }) {
  return (
    <div className="flex items-center gap-3 rounded-2xl border-[3px] border-tiger-deep bg-tiger-cream px-3 py-2 shadow-sticker">
      <Medal rank={rank} />
      <div className="flex-1 min-w-0">
        <p className="truncate font-heading text-base font-bold text-tiger-deep">
          {name}
        </p>
        {meta && (
          <p className="truncate font-body text-xs text-tiger-deep/70">{meta}</p>
        )}
      </div>
      {primary}
    </div>
  );
}
